import { type TeamData, TeamDataSchema } from '../entity'
import { TeamIdSchema, type TeamId } from '../ids'
import { RoleIdSchema, type RoleId } from '../role'
import { z } from 'zod'

export const TERROS_TECH_TEAM_ID: TeamId = 'Team:tech'
export const TERROS_CX_TEAM_ID: TeamId = 'Team:cx'
export const UNASSIGNED_TEAM_ID: TeamId = 'Team.unassigned'
export function isUnassignedTeamId(teamId: TeamId): boolean {
  return teamId === UNASSIGNED_TEAM_ID
}

export const MicroMembershipSchema = z.object({
  teamId: TeamIdSchema,
  roleIds: z.array(RoleIdSchema).optional(),
})
export type MicroMembership = z.infer<typeof MicroMembershipSchema>

export const MicroTeamSchema = TeamDataSchema.pick({
  teamId: true,
  parentId: true,
  restrictVisibility: true,
})
export type MicroTeam = z.infer<typeof MicroTeamSchema>

/** A team with its place in the org tree, as the caller sees it (`/team/list`, `/team/get`). */
export const TeamNodeSchema = z.object({
  ...TeamDataSchema.shape,
  childTeamIds: z.array(TeamIdSchema).meta({
    id: 'teamChildTeamIds',
    description: 'IDs of the direct child teams visible to the caller.',
  }),
  isLeaf: z.boolean().meta({
    id: 'teamIsLeaf',
    description:
      'True when the caller sees no child teams under this team: the bottom of the org chart from their point of view.',
  }),
  levelName: z.string().optional().meta({
    id: 'teamLevelName',
    description:
      "The company's name for this team's level, such as Region or Office. Omitted when the level has no name.",
    example: 'Office',
  }),
})
export type TeamNode = z.infer<typeof TeamNodeSchema>

export const PartialTeamDataSchema = TeamDataSchema.partial().extend({
  teamId: TeamIdSchema,
})
export type PartialTeamData = Partial<TeamData> & WithTeamId

export type TeamFilterData = Pick<TeamData, 'teamId' | 'name'>

export const WithTeamIdSchema = z.object({ teamId: TeamIdSchema })
export type WithTeamId = z.infer<typeof WithTeamIdSchema>

export const DEFAULT_MANAGER_BASED_TEAMS_LEVEL = 1
